<template>
  <v-card class="mb-8" elevation="0">
    <v-card-text>
      <div class="subtitle-1 font-weight-bold mb-4">
        <v-icon left>mdi-food-variant</v-icon>
        {{ $t("select_what_can_be_ordered") }}
      </div>

      <div>
        <v-card
          v-for="(type, index) in whatCanOrderTypes"
          :key="type.value"
          outlined
          class="pa-4"
          :class="{ 'mb-3': index !== whatCanOrderTypes.length - 1 }"
        >
          <v-checkbox
            :value="type.value"
            :input-value="selectedOfferTypes"
            @change="toggleOfferType(type.value)"
            hide-details
            :readonly="readonly"
          >
            <template v-slot:label>
              <div>
                <v-icon left :color="type.color">{{ type.icon }}</v-icon>
                <strong>{{ $t(type.i18nKey) }}</strong>
                <div class="text-caption grey--text ml-8">{{ $t(type.descKey) }}</div>
              </div>
            </template>
          </v-checkbox>

          <v-expand-transition>
            <div
              v-if="
                selectedOfferTypes.includes(type.value) &&
                type.value === 'group_selection'
              "
              class="mt-3 ml-8"
            >
              <div>
                <v-checkbox
                  v-model="config.ordering_rules.settings.is_strict_composition"
                  :label="$t('different_meal_count_per_group')"
                  hide-details
                  class="mb-3"
                  :readonly="readonly"
                />

                <v-expand-transition>
                  <div v-if="!isStrictComposition">
                    <v-row>
                      <v-col cols="12" sm="6">
                        <text-field
                          v-model.number="config.ordering_rules.settings.meals_per_group"
                          :label="$t('meals_per_group')"
                          type="number"
                          outlined
                          dense
                          :hint="$t('number_of_meals_per_group')"
                          persistent-hint
                          :readonly="readonly"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>

                <v-expand-transition>
                  <div v-if="isStrictComposition">
                    <div class="subtitle-2 mb-3">
                      {{ $t("set_meal_count_per_group") }}
                    </div>

                    <div v-if="loadingCategories" class="text-center py-4">
                      <v-progress-circular indeterminate color="primary" />
                    </div>

                    <v-row v-else>
                      <v-col
                        v-for="category in categories"
                        :key="category.id"
                        cols="12"
                        sm="4"
                      >
                        <text-field
                          :value="getStrictCompositionValue(category.id)"
                          @input="setStrictCompositionValue(category.id, $event)"
                          :label="toLatin(category.name)"
                          type="number"
                          outlined
                          dense
                          :hint="$t('number_of_meals_per_group')"
                          persistent-hint
                          :readonly="readonly"
                        />
                      </v-col>
                    </v-row>
                  </div>
                </v-expand-transition>
              </div>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div
              v-if="
                selectedOfferTypes.includes(type.value) && type.value === 'daily_menu'
              "
              class="mt-3 ml-8"
            >
              <div class="subtitle-2">{{ $t("limit_packages") }}</div>
              <v-radio-group
                v-model="config.ordering_rules.settings.packages"
                hide-details
                class="mt-1"
                :readonly="readonly"
              >
                <v-radio
                  v-for="option in packagesOptions"
                  :key="option.value"
                  :value="option.value"
                  :label="$t(option.i18nKey)"
                />
              </v-radio-group>
            </div>
          </v-expand-transition>
        </v-card>

        <v-alert
          v-if="selectedOfferTypes.length === 0"
          type="error"
          text
          dense
          class="mt-3 mb-0"
        >
          {{ $t("select_at_least_one_option") }}
        </v-alert>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import SelectableCard from "../cards/SelectableCard.vue";
import { WHAT_CAN_ORDER_TYPES, PACKAGES_OPTIONS } from "@/utils/limitHelpers";
import categoryService from "@/services/category";

export default {
  name: "Step2WhatCanOrder",
  components: {
    SelectableCard,
  },
  props: {
    config: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      categories: [],
      loadingCategories: false,
    };
  },
  mounted() {
    this.ensureOrderingRulesStructure();
    this.fetchCategories();
  },
  computed: {
    whatCanOrderTypes() {
      return WHAT_CAN_ORDER_TYPES;
    },
    packagesOptions() {
      return PACKAGES_OPTIONS;
    },
    selectedOfferTypes() {
      const selected = this.config.ordering_rules.active;
      return Array.isArray(selected) ? selected : selected ? [selected] : [];
    },
    isStrictComposition() {
      return this.config.ordering_rules.settings.is_strict_composition;
    },
  },
  methods: {
    ensureOrderingRulesStructure() {
      if (!this.config.ordering_rules || typeof this.config.ordering_rules !== "object") {
        this.$set(this.config, "ordering_rules", {});
      }

      if (!Array.isArray(this.config.ordering_rules.active)) {
        this.$set(this.config.ordering_rules, "active", []);
      }

      if (
        !this.config.ordering_rules.settings ||
        typeof this.config.ordering_rules.settings !== "object"
      ) {
        this.$set(this.config.ordering_rules, "settings", {});
      }

      if (
        !this.config.ordering_rules.settings.strict_composition ||
        typeof this.config.ordering_rules.settings.strict_composition !== "object" ||
        Array.isArray(this.config.ordering_rules.settings.strict_composition)
      ) {
        this.$set(this.config.ordering_rules.settings, "strict_composition", {});
      }

      if (!PACKAGES_OPTIONS.some((o) => o.value === this.config.ordering_rules.settings.packages)) {
        this.$set(this.config.ordering_rules.settings, "packages", "all");
      }

      if (
        typeof this.config.ordering_rules.settings.is_strict_composition !== "boolean"
      ) {
        const legacyEnabled = Boolean(
          this.config.ordering_rules.group_selection?.enabled
        );
        this.$set(
          this.config.ordering_rules.settings,
          "is_strict_composition",
          legacyEnabled
        );
      }
    },
    getStrictCompositionValue(categoryId) {
      this.ensureOrderingRulesStructure();
      const strictComposition = this.config.ordering_rules.settings.strict_composition;
      const key = String(categoryId);
      return strictComposition[key] ?? null;
    },
    setStrictCompositionValue(categoryId, value) {
      this.ensureOrderingRulesStructure();
      const strictComposition = this.config.ordering_rules.settings.strict_composition;
      const key = String(categoryId);

      if (value === "" || value === null || value === undefined) {
        this.$delete(strictComposition, key);
        return;
      }

      const normalizedValue = Number(value);
      this.$set(
        strictComposition,
        key,
        Number.isFinite(normalizedValue) ? normalizedValue : 0
      );
    },
    toggleOfferType(value) {
      this.ensureOrderingRulesStructure();
      let selected = [...this.selectedOfferTypes];
      const index = selected.indexOf(value);

      if (index > -1) {
        // Ukloni ako je već selektovan
        selected.splice(index, 1);
      } else {
        // Dodaj novi
        selected.push(value);

        // Validacija: ne mogu zajedno 'full_meal' i 'group_selection'
        if (selected.includes("full_meal") && selected.includes("group_selection")) {
          // Ukloni prethodno selektovanu opciju
          if (value === "full_meal") {
            selected = selected.filter((v) => v !== "group_selection");
          } else if (value === "group_selection") {
            selected = selected.filter((v) => v !== "full_meal");
          }
        }
      }

      this.$set(this.config.ordering_rules, "active", selected);
    },
    async fetchCategories() {
      try {
        this.loadingCategories = true;
        const response = await categoryService.getCategories();
        this.categories = response.data || [];
      } catch (error) {
        console.error("Error fetching categories:", error);
        this.categories = [];
      } finally {
        this.loadingCategories = false;
      }
    },
  },
};
</script>
